/**
 * Partner detail drawer component.
 *
 * Displays detailed information about a partner in a slide-out drawer.
 * Shows comprehensive partner profile including availability, location,
 * topics of interest, and provides messaging functionality.
 *
 * @module PartnerDetailDrawer
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Clock, Flag, Globe, MapPin, MessageCircle } from "lucide-react";
import { Partner } from "@/types/partner-finder";
import { ReportUserDialog } from "./ReportUserDialog";
import {
  translateMeetingPreference,
  translatePartnerDays,
  translatePartnerGoal,
  translatePartnerLocation,
} from "@/lib/partnerFinderLabels";

interface PartnerDetailDrawerProps {
  readonly partner: Partner;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onMessage: () => void;
}

export default function PartnerDetailDrawer({
  partner,
  open,
  onOpenChange,
  onMessage,
}: PartnerDetailDrawerProps) {
  const { t } = useTranslation();
  const [reportOpen, setReportOpen] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const availabilityText = translatePartnerDays(t, partner.availability);
  const meetingPreferenceLabel = translateMeetingPreference(
    t,
    partner.meetingPreference,
  );

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[calc(100dvh-1rem)]">
        <DrawerHeader className="border-b">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 shrink-0">
              <AvatarFallback className="text-lg">
                {getInitials(partner.displayName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <DrawerTitle className="text-2xl">
                {partner.displayName}
              </DrawerTitle>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge variant="outline">{partner.level}</Badge>
                <Badge variant="secondary">
                  {translatePartnerGoal(t, partner.goal)}
                </Badge>
                <Badge variant="outline">
                  {partner.meetingPreference === "online" ? (
                    <>
                      <Globe className="h-3 w-3 mr-1 inline" />
                      {meetingPreferenceLabel}
                    </>
                  ) : (
                    <>
                      <MapPin className="h-3 w-3 mr-1 inline" />
                      {meetingPreferenceLabel}
                    </>
                  )}
                </Badge>
              </div>
            </div>
          </div>
        </DrawerHeader>

        <div className="px-4 pb-4 overflow-y-auto space-y-6 pt-6">
          {/* About */}
          <section>
            <h3 className="font-semibold mb-2">
              {t("partnerFinder.details.about", { defaultValue: "About" })}
            </h3>
            <p className="text-muted-foreground">{partner.about}</p>
          </section>

          {/* Topics of Interest */}
          {partner.topicsOfInterest && partner.topicsOfInterest.length > 0 && (
            <section>
              <h3 className="font-semibold mb-2">
                {t("partnerFinder.details.topicsOfInterest", {
                  defaultValue: "Topics of Interest",
                })}
              </h3>
              <div className="flex flex-wrap gap-2">
                {partner.topicsOfInterest.map((topic) => (
                  <Badge key={topic} variant="secondary">
                    {topic}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          {/* Availability Details */}
          <section>
            <h3 className="font-semibold mb-3">
              {t("partnerFinder.search.availabilityLabel", {
                defaultValue: "Availability",
              })}
            </h3>
            <div className="space-y-2">
              {partner.availability.length > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{availabilityText}</span>
                </div>
              )}
              {partner.location && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{translatePartnerLocation(t, partner.location)}</span>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="px-4 pb-4 border-t pt-4 space-y-2">
          <Button onClick={onMessage} className="w-full" size="lg">
            <MessageCircle className="h-4 w-4 mr-2" />
            {t("partnerFinder.buttons.message", { defaultValue: "Message" })}
          </Button>
          {partner.userId && (
            <Button
              onClick={() => setReportOpen(true)}
              variant="ghost"
              className="w-full text-muted-foreground hover:text-destructive"
              size="sm"
            >
              <Flag className="h-4 w-4 mr-2" />
              {t("moderation.menu.report", { defaultValue: "Report user" })}
            </Button>
          )}
        </div>
      </DrawerContent>

      {partner.userId && (
        <ReportUserDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          reportedUserId={partner.userId}
          reportedUserName={partner.displayName}
        />
      )}
    </Drawer>
  );
}
